/** @format **/

/**
 * External dependencies
 */
import React from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { createInterpolateElement } from '@wordpress/element';
import { Icon, Button } from '@wordpress/components';
import { envelope, comment, page } from '@wordpress/icons';

/**
 * Internal dependencies
 */
import type { ChargeBillingDetails } from 'wcpay/types/charges';
import type { Dispute } from 'wcpay/types/disputes';
import { formatExplicitCurrency } from 'multi-currency/interface/functions';
import { formatDateTimeFromTimestamp } from 'wcpay/utils/date-time';
import InlineNotice from 'components/inline-notice';
import {
	Accordion,
	AccordionBody,
	AccordionRow,
} from 'wcpay/components/accordion';
import DisputeStepItem from 'wcpay/components/dispute-step-item';

interface Props {
	dispute: Pick<
		Dispute,
		| 'amount'
		| 'created'
		| 'currency'
		| 'evidence_details'
		| 'reason'
		| 'status'
	>;
	customer: ChargeBillingDetails | null;
	chargeCreated: number;
	bankName: string | null;
}

interface LearnMoreButtonProps {
	href: string;
}

const LearnMoreButton: React.FC< LearnMoreButtonProps > = ( { href } ) => {
	return (
		<Button
			variant="secondary"
			href={ href }
			target="_blank"
			rel="noopener noreferrer"
		>
			{ __( 'Learn more', 'woocommerce-payments' ) + ' ' }
			&#8599;
		</Button>
	);
};

export const DisputeSteps: React.FC< Props > = ( {
	dispute,
	customer,
	chargeCreated,
	bankName,
} ) => {
	let emailLink;
	if ( customer?.email ) {
		const chargeDate = formatDateTimeFromTimestamp( chargeCreated );
		const disputeDate = formatDateTimeFromTimestamp( dispute.created );
		const emailSubject = sprintf(
			// Translators: %1$s is the store name, %2$s is the charge date.
			__(
				`Problem with your purchase from %1$s on %2$s?`,
				'woocommerce-payments'
			),
			wcpaySettings.storeName,
			chargeDate
		);
		const customerName = customer?.name || '';
		const emailBody = sprintf(
			// Translators: %1$s is the customer name, %2$s is the dispute date, %3$s is the dispute amount with currency-code e.g. $15 USD, %4$s is the charge date.
			__(
				`Hello %1$s,\n\n` +
					`We noticed that on %2$s, you disputed a %3$s charge on %4$s. We wanted to contact you to make sure everything was all right with your purchase and see if there's anything else we can do to resolve any problems you might have had.\n\n` +
					`Alternatively, if the dispute was a mistake, you can easily withdraw it by calling the number on the back of your card. Thank you so much - we appreciate your business and look forward to working with you.`,
				'woocommerce-payments'
			),
			customerName,
			disputeDate,
			formatExplicitCurrency( dispute.amount, dispute.currency ),
			chargeDate
		);
		emailLink = `mailto:${ customer.email }?subject=${ encodeURIComponent(
			emailSubject
		) }&body=${ encodeURIComponent( emailBody ) }`;
	}

	return (
		<div className="dispute-steps">
			<Accordion>
				<AccordionBody
					lg
					title={ __( 'Steps you can take', 'woocommerce-payments' ) }
					subtitle={ __(
						'We recommend reviewing your options before responding before the deadline. ',
						'woocommerce-payments'
					) }
				>
					<AccordionRow>
						<div className="dispute-steps__content">
							<div className="dispute-steps__items">
								<DisputeStepItem
									icon={ <Icon icon={ envelope } /> }
									title={ __(
										'Contact your customer',
										'woocommerce-payments'
									) }
									description={ __(
										'Identify the issue and work towards a resolution where possible.',
										'woocommerce-payments'
									) }
									action={
										customer?.email ? (
											<Button
												variant="secondary"
												href={ emailLink }
												target="_blank"
												rel="noopener noreferrer"
											>
												{ __(
													'Email customer',
													'woocommerce-payments'
												) }
											</Button>
										) : null
									}
								/>
								<DisputeStepItem
									icon={ <Icon icon={ comment } /> }
									title={ __(
										'Ask for the dispute to be withdrawn',
										'woocommerce-payments'
									) }
									description={ __(
										"If you've managed to resolve the issue with your customer, help them with the withdrawal of their dispute.",
										'woocommerce-payments'
									) }
									action={
										<LearnMoreButton href="https://woocommerce.com/document/woopayments/fraud-and-disputes/managing-disputes/#withdrawals" />
									}
								/>
								<DisputeStepItem
									icon={ <Icon icon={ page } /> }
									title={ __(
										'Challenge or accept the dispute',
										'woocommerce-payments'
									) }
									description={ __(
										"Disagree with the dispute? You can challenge it with the customer's bank. Otherwise, accept it to close the case — the order amount and dispute fee won't be refunded.",
										'woocommerce-payments'
									) }
								/>
							</div>

							{ /* Dispute notice */ }
							<div className="dispute-steps__notice">
								<InlineNotice
									icon
									isDismissible={ false }
									status="info"
									className="dispute-steps__notice-content"
								>
									{ createInterpolateElement(
										bankName
											? sprintf(
													__(
														'<strong>The outcome of this dispute will be determined by %1$s.</strong> WooPayments has no influence over the decision and is not liable for any chargebacks.',
														'woocommerce-payments'
													),
													bankName
											  )
											: __(
													"<strong>The outcome of this dispute will be determined by the cardholder's bank.</strong> WooPayments has no influence over the decision and is not liable for any chargebacks.",
													'woocommerce-payments'
											  ),
										{
											strong: <strong />,
										}
									) }
								</InlineNotice>
							</div>
						</div>
					</AccordionRow>
				</AccordionBody>
			</Accordion>
		</div>
	);
};

export const NonCompliantDisputeSteps: React.FC = () => {
	return (
		<div className="dispute-steps">
			<Accordion defaultExpanded={ true }>
				<AccordionBody
					lg
					title={ __( 'Steps you can take', 'woocommerce-payments' ) }
					subtitle={ __(
						'We recommend reviewing your options before responding by the deadline. ',
						'woocommerce-payments'
					) }
				>
					<AccordionRow>
						<div className="dispute-steps__content">
							<div className="dispute-steps__items">
								<DisputeStepItem
									icon={ <Icon icon={ page } /> }
									title={ __(
										'Accepting the dispute',
										'woocommerce-payments'
									) }
									description={ __(
										'Accepting the dispute means you’ll forfeit the funds, pay the standard dispute fee, and avoid the $500 USD Visa network fee.',
										'woocommerce-payments'
									) }
									action={
										<LearnMoreButton href="https://woocommerce.com/document/woopayments/fraud-and-disputes/managing-disputes/#visa-compliance-disputes" />
									}
								/>
								<DisputeStepItem
									icon={ <Icon icon={ envelope } /> }
									title={ __(
										'Challenge the dispute',
										'woocommerce-payments'
									) }
									description={ __(
										'Challenging the dispute will incur a $500 USD Visa network fee, which is charged when you submit evidence. This fee will be refunded if you win the dispute.',
										'woocommerce-payments'
									) }
									action={
										<LearnMoreButton href="https://woocommerce.com/document/woopayments/fraud-and-disputes/managing-disputes/#visa-compliance-disputes" />
									}
								/>
							</div>

							{ /* Dispute notice */ }
							<div className="dispute-steps__notice">
								<InlineNotice
									icon
									isDismissible={ false }
									status="info"
									className="dispute-steps__notice-content"
								>
									{ createInterpolateElement(
										__(
											'<strong>The outcome of this dispute will be determined by Visa.</strong> WooPayments has no influence over the decision and is not liable for any chargebacks.',
											'woocommerce-payments'
										),
										{
											strong: <strong />,
										}
									) }
								</InlineNotice>
							</div>
						</div>
					</AccordionRow>
				</AccordionBody>
			</Accordion>
		</div>
	);
};

export const InquirySteps: React.FC< Props > = ( {
	dispute,
	customer,
	chargeCreated,
	bankName,
} ) => {
	let emailLink;
	if ( customer?.email ) {
		const chargeDate = formatDateTimeFromTimestamp( chargeCreated, {
			includeTime: true,
		} );
		const disputeDate = formatDateTimeFromTimestamp( dispute.created, {
			includeTime: true,
		} );
		const emailSubject = sprintf(
			// Translators: %1$s is the store name, %2$s is the charge date.
			__(
				`Problem with your purchase from %1$s on %2$s?`,
				'woocommerce-payments'
			),
			wcpaySettings.storeName,
			chargeDate
		);
		const customerName = customer?.name || '';
		const emailBody = sprintf(
			// Translators: %1$s is the customer name, %2$s is the dispute date, %3$s is the dispute amount with currency-code e.g. $15 USD, %4$s is the charge date.
			__(
				`Hello %1$s,\n\n` +
					`We noticed that on %2$s, you raised a question with your payment provider about a %3$s charge made on %4$s. We wanted to reach out to ensure everything is all right with your purchase and to see if there's anything we can do to resolve any problems you might have had.\n\n` +
					`Alternatively, if this was a mistake, please contact your payment provider to resolve it. Thank you so much - we appreciate your business and look forward to working with you.`,
				'woocommerce-payments'
			),
			customerName,
			disputeDate,
			formatExplicitCurrency( dispute.amount, dispute.currency ),
			chargeDate
		);
		emailLink = `mailto:${ customer.email }?subject=${ encodeURIComponent(
			emailSubject
		) }&body=${ encodeURIComponent( emailBody ) }`;
	}

	return (
		<div className="dispute-steps">
			<Accordion>
				<AccordionBody
					lg
					title={ __( 'Steps you can take', 'woocommerce-payments' ) }
					subtitle={ __(
						'We recommend reviewing your options before responding by the deadline. ',
						'woocommerce-payments'
					) }
				>
					<AccordionRow>
						<div className="dispute-steps__content">
							<div className="dispute-steps__items">
								<DisputeStepItem
									icon={ <Icon icon={ envelope } /> }
									title={ __(
										'Contact your customer',
										'woocommerce-payments'
									) }
									description={ __(
										'Identify the issue and work towards a resolution where possible.',
										'woocommerce-payments'
									) }
									action={
										customer?.email ? (
											<Button
												variant="secondary"
												href={ emailLink }
												target="_blank"
												rel="noopener noreferrer"
											>
												{ __(
													'Email customer',
													'woocommerce-payments'
												) }
											</Button>
										) : null
									}
								/>
								<DisputeStepItem
									icon={ <Icon icon={ page } /> }
									title={ __(
										'Submit evidence or issue a refund',
										'woocommerce-payments'
									) }
									description={ __(
										"Disagree with the claim? You can challenge it by submitting evidence to the customer's bank. Otherwise, you can settle the inquiry by issuing a refund.",
										'woocommerce-payments'
									) }
									action={
										<LearnMoreButton href="https://woocommerce.com/document/woopayments/fraud-and-disputes/managing-disputes/#inquiries" />
									}
								/>
							</div>

							{ /* Inquiry notice */ }
							<div className="dispute-steps__notice">
								<InlineNotice
									icon
									isDismissible={ false }
									status="info"
									className="dispute-steps__notice-content"
								>
									{ createInterpolateElement(
										bankName
											? sprintf(
													__(
														'<strong>The outcome of this inquiry will be determined by %1$s.</strong> WooPayments has no influence over the decision and is not liable for any chargebacks.',
														'woocommerce-payments'
													),
													bankName
											  )
											: __(
													"<strong>The outcome of this inquiry will be determined by the cardholder's bank.</strong> WooPayments has no influence over the decision and is not liable for any chargebacks.",
													'woocommerce-payments'
											  ),
										{
											strong: <strong />,
										}
									) }
								</InlineNotice>
							</div>
						</div>
					</AccordionRow>
				</AccordionBody>
			</Accordion>
		</div>
	);
};

export const NotDefendableInquirySteps: React.FC< Props > = ( {
	dispute,
	customer,
	chargeCreated,
	bankName,
} ) => {
	let emailLink;
	if ( customer?.email ) {
		const chargeDate = formatDateTimeFromTimestamp( chargeCreated, {
			includeTime: true,
		} );
		const disputeDate = formatDateTimeFromTimestamp( dispute.created, {
			includeTime: true,
		} );
		const emailSubject = sprintf(
			// Translators: %1$s is the store name, %2$s is the charge date.
			__(
				`Problem with your purchase from %1$s on %2$s?`,
				'woocommerce-payments'
			),
			wcpaySettings.storeName,
			chargeDate
		);
		const customerName = customer?.name || '';
		const emailBody = sprintf(
			// Translators: %1$s is the customer name, %2$s is the dispute date, %3$s is the dispute amount with currency-code e.g. $15 USD, %4$s is the charge date.
			__(
				`Hello %1$s,\n\n` +
					`We noticed that on %2$s, you raised a question with your payment provider about a %3$s charge made on %4$s. We wanted to reach out to ensure everything is all right with your purchase and to see if there's anything we can do to resolve any problems you might have had.\n\n` +
					`Alternatively, if this was a mistake, please contact your payment provider to resolve it. Thank you so much - we appreciate your business and look forward to working with you.`,
				'woocommerce-payments'
			),
			customerName,
			disputeDate,
			formatExplicitCurrency( dispute.amount, dispute.currency ),
			chargeDate
		);
		emailLink = `mailto:${ customer.email }?subject=${ encodeURIComponent(
			emailSubject
		) }&body=${ encodeURIComponent( emailBody ) }`;
	}

	const isReturn = dispute.reason === 'credit_not_processed';

	return (
		<div className="dispute-steps">
			<Accordion>
				<AccordionBody
					lg
					title={ __( 'Steps you can take', 'woocommerce-payments' ) }
					subtitle={ __(
						'We recommend reviewing your options before responding by the deadline. ',
						'woocommerce-payments'
					) }
				>
					<AccordionRow>
						<div className="dispute-steps__content">
							<div className="dispute-steps__items">
								<DisputeStepItem
									icon={ <Icon icon={ envelope } /> }
									title={ __(
										'Contact your customer',
										'woocommerce-payments'
									) }
									description={
										isReturn
											? __(
													"Reach out to the customer to check if they're returning the item(s).",
													'woocommerce-payments'
											  )
											: __(
													'Identify the issue and work towards a resolution where possible.',
													'woocommerce-payments'
											  )
									}
									action={
										customer?.email ? (
											<Button
												variant="secondary"
												href={ emailLink }
												target="_blank"
												rel="noopener noreferrer"
											>
												{ __(
													'Email customer',
													'woocommerce-payments'
												) }
											</Button>
										) : null
									}
								/>
								<DisputeStepItem
									icon={ <Icon icon={ page } /> }
									title={ __(
										'Issue a refund',
										'woocommerce-payments'
									) }
									description={
										isReturn
											? __(
													"Once you've received the item(s), refund the customer before the deadline to prevent this escalating to a dispute.",
													'woocommerce-payments'
											  )
											: __(
													'If appropriate, issue a refund to resolve the inquiry before the deadline.',
													'woocommerce-payments'
											  )
									}
								/>
								{ isReturn && (
									<DisputeStepItem
										icon={ <Icon icon={ envelope } /> }
										title={ __(
											'Respond when the inquiry becomes a dispute',
											'woocommerce-payments'
										) }
										description={ __(
											"If the returned item(s) aren't received, the inquiry may escalate to a dispute after 21 days. You can then submit evidence and challenge it (a dispute fee applies), or accept the dispute and forfeit the funds.",
											'woocommerce-payments'
										) }
										action={
											<LearnMoreButton href="https://woocommerce.com/document/woopayments/payment-methods/buy-now-pay-later/#klarna-inquiries-returns" />
										}
									/>
								) }
							</div>

							{ /* Inquiry notice */ }
							<div className="dispute-steps__notice">
								<InlineNotice
									icon
									isDismissible={ false }
									status="info"
									className="dispute-steps__notice-content"
								>
									{ createInterpolateElement(
										sprintf(
											/* translators: %s is the payment provider name, eg "Klarna". */
											__(
												'<strong>The outcome of this inquiry will be determined by %s.</strong> WooPayments has no influence over the decision and is not liable for any chargebacks.',
												'woocommerce-payments'
											),
											bankName
										),
										{
											strong: <strong />,
										}
									) }
								</InlineNotice>
							</div>
						</div>
					</AccordionRow>
				</AccordionBody>
			</Accordion>
		</div>
	);
};
